import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Building, Handshake, Network, UserRound } from "lucide-react";
import { Eyebrow, Reveal } from "../motion/Reveal";
import { useSite } from "../../lib/site";

const VALUE_ICONS = [UserRound, Handshake, Building, Network];
const VALUE_TONES = ["bg-leaf text-ink", "bg-sun text-ink", "bg-azure text-white", "bg-coral text-white"];

function toEmbed(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}

const Video = ({ url, title }) => {
  const src = toEmbed(url);
  if (!src) return null;
  const isFile = /\.(mp4|webm|ogg)(\?|$)/i.test(src);
  return (
    <Reveal className="mt-20 overflow-hidden rounded-[2rem] border border-ink/10 bg-ink" data-testid="filosofia-video">
      <div className="aspect-video w-full">
        {isFile ? (
          <video src={src} controls className="h-full w-full object-cover" />
        ) : (
          <iframe src={src} title={title} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture" allowFullScreen />
        )}
      </div>
    </Reveal>
  );
};

export const Philosophy = () => {
  const { content } = useSite();
  const f = content.filosofia;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#FBFAF5] py-24 sm:py-32 lg:py-40" data-testid="filosofia-section">
      <div className="container-x">
        <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
          <Reveal className="relative lg:col-span-5">
            <div className="relative mx-auto aspect-[3/4] w-full max-w-[460px] overflow-hidden rounded-t-full border border-ink/10">
              <motion.img style={{ y, scale: 1.22 }} src={content.media.filosofia_image} alt="Equipo comercial trabajando con tecnología" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-ink px-5 py-3 font-mono text-xs uppercase tracking-[0.14em] text-white">
              Tecnología · Personas · Valor
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Eyebrow tone="light">{f.eyebrow}</Eyebrow>
            <h2 className="mt-6" data-testid="filosofia-title">
              <span className="sr-only">Happy People Factory</span>
              <img src="/brand/logo-color.png" alt="" aria-hidden="true" className="h-auto w-full max-w-[560px] select-none" draggable={false} data-testid="filosofia-logo" />
            </h2>
            <Reveal className="mt-10">
              <p className="font-mono text-xs uppercase tracking-[0.14em] text-ink/70">{f.meaning_label}</p>
              <p className="mt-2 font-display text-3xl font-semibold uppercase tracking-tight sm:text-5xl">“{f.meaning}”</p>
              <p className="mt-4 font-display text-2xl font-medium italic text-leaf-deep sm:text-3xl">{f.twist}</p>
            </Reveal>
            <Reveal className="mt-8 space-y-4 text-lg leading-relaxed text-ink/80">
              {f.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </Reveal>
          </div>
        </div>
        <div className="mt-20 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {f.values.map((v, i) => {
            const Icon = VALUE_ICONS[i % 4];
            return (
              <Reveal key={i} delay={i * 0.08} className="rounded-[1.5rem] border border-ink/10 bg-white p-7" data-testid={`filosofia-value-${i}`}>
                <span className={`grid h-12 w-12 place-items-center rounded-2xl ${VALUE_TONES[i % 4]}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <p className="mt-8 font-display text-2xl font-semibold tracking-tight">{v.title}</p>
                <p className="mt-1 text-base text-ink/75">{v.text}</p>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="mt-14 max-w-3xl">
          <p className="font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl">{f.closing}</p>
        </Reveal>
        <Video url={content.media.video_url} title={content.media.video_title} />
      </div>
    </section>
  );
};
